(() => {
  const initializeStyleGuides = (scope = document) => {
    const guides = scope.matches?.('[data-style-guide]') ? [scope] : [...scope.querySelectorAll('[data-style-guide]')];

    guides.forEach((guide) => {
      if (guide.dataset.styleGuideReady === 'true') return;
      guide.dataset.styleGuideReady = 'true';

      const tabs = [...guide.querySelectorAll('[data-style-tab]')];
      const panels = [...guide.querySelectorAll('[data-style-panel]')];
      const panelsTrack = guide.querySelector('[data-style-panels]');
      const count = guide.querySelector('[data-style-count]');
      const live = guide.querySelector('[data-style-live]');
      const previous = guide.querySelector('[data-style-prev]');
      const next = guide.querySelector('[data-style-next]');
      let activeIndex = Math.max(0, tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));

      const selectStyle = (index, moveFocus = false, shouldScroll = true) => {
        if (!tabs.length) return;
        activeIndex = Math.max(0, Math.min(tabs.length - 1, index));
        tabs.forEach((tab, tabIndex) => {
          const selected = tabIndex === activeIndex;
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
          tab.classList.toggle('is-active', selected);
        });
        panels.forEach((panel, panelIndex) => {
          const selected = panelIndex === activeIndex;
          panel.classList.toggle('is-active', selected);
          panel.setAttribute('aria-hidden', String(!selected));
          panel.inert = !selected;
        });
        if (panelsTrack && shouldScroll && window.matchMedia('(max-width: 767px)').matches) {
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          panelsTrack.scrollTo({ left: activeIndex * panelsTrack.clientWidth, behavior: reducedMotion ? 'auto' : 'smooth' });
        }
        if (count) count.textContent = String(activeIndex + 1).padStart(2, '0');
        if (live) live.textContent = `Slide ${activeIndex + 1} of ${tabs.length}: ${tabs[activeIndex].textContent.trim()}`;
        if (previous) previous.disabled = activeIndex === 0;
        if (next) next.disabled = activeIndex === tabs.length - 1;
        if (moveFocus) tabs[activeIndex].focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => selectStyle(index));
        tab.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            selectStyle((activeIndex + 1) % tabs.length, true);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            selectStyle((activeIndex - 1 + tabs.length) % tabs.length, true);
          } else if (event.key === 'Home') {
            event.preventDefault();
            selectStyle(0, true);
          } else if (event.key === 'End') {
            event.preventDefault();
            selectStyle(tabs.length - 1, true);
          }
        });
      });

      previous?.addEventListener('click', () => selectStyle(activeIndex - 1));
      next?.addEventListener('click', () => selectStyle(activeIndex + 1));
      panelsTrack?.addEventListener('scroll', () => {
        if (!panelsTrack.clientWidth) return;
        const index = Math.round(panelsTrack.scrollLeft / panelsTrack.clientWidth);
        if (index !== activeIndex) selectStyle(index, false, false);
      }, { passive: true });
      selectStyle(activeIndex, false, false);
    });
  };

  const initializeHeroCarousels = (scope = document) => {
    const carousels = scope.matches?.('[data-mj-hero-carousel]') ? [scope] : [...scope.querySelectorAll('[data-mj-hero-carousel]')];

    carousels.forEach((carousel) => {
      if (carousel.dataset.mjHeroCarouselReady === 'true') return;
      carousel.dataset.mjHeroCarouselReady = 'true';

      const slides = [...carousel.querySelectorAll('[data-mj-hero-slide]')];
      const indicators = [...carousel.querySelectorAll('[data-mj-hero-indicator]')];
      const previous = carousel.querySelector('[data-mj-hero-prev]');
      const next = carousel.querySelector('[data-mj-hero-next]');
      const current = carousel.querySelector('[data-mj-hero-current]');
      const media = window.matchMedia('(prefers-reduced-motion: reduce)');
      const interval = Math.max(1000, Number(carousel.dataset.interval) || 6000);
      const autoplay = carousel.dataset.autoplay === 'true';
      let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let timer = 0;
      let pointerStartX = null;
      let hovered = false;
      let focused = false;

      const clearTimer = () => {
        window.clearTimeout(timer);
        timer = 0;
      };
      const schedule = () => {
        clearTimer();
        if (!autoplay || media.matches || hovered || focused || slides.length < 2) return;
        timer = window.setTimeout(() => showSlide(activeIndex + 1), interval);
      };
      const showSlide = (requestedIndex) => {
        if (!slides.length) return;
        activeIndex = (requestedIndex + slides.length) % slides.length;
        slides.forEach((slide, index) => {
          const selected = index === activeIndex;
          slide.classList.toggle('is-active', selected);
          slide.setAttribute('aria-hidden', String(!selected));
          slide.inert = !selected;
        });
        indicators.forEach((indicator, index) => {
          const selected = index === activeIndex;
          indicator.classList.toggle('is-active', selected);
          indicator.setAttribute('aria-current', String(selected));
        });
        if (current) current.textContent = String(activeIndex + 1).padStart(2, '0');
        schedule();
      };
      const handlePreferenceChange = () => schedule();
      const handlePointerDown = (event) => {
        if (event.pointerType !== 'touch' || event.target.closest('button, a')) return;
        pointerStartX = event.clientX;
        carousel.setPointerCapture(event.pointerId);
      };
      const handlePointerUp = (event) => {
        if (pointerStartX === null) return;
        const distance = event.clientX - pointerStartX;
        pointerStartX = null;
        if (Math.abs(distance) >= 50) showSlide(activeIndex + (distance < 0 ? 1 : -1));
      };
      const handleFocusOut = (event) => {
        if (carousel.contains(event.relatedTarget)) return;
        focused = false;
        schedule();
      };
      const handleKeyDown = (event) => {
        if (event.target !== carousel) return;
        if (event.key === 'ArrowRight') showSlide(activeIndex + 1);
        if (event.key === 'ArrowLeft') showSlide(activeIndex - 1);
      };

      previous?.addEventListener('click', () => showSlide(activeIndex - 1));
      next?.addEventListener('click', () => showSlide(activeIndex + 1));
      indicators.forEach((indicator, index) => indicator.addEventListener('click', () => showSlide(index)));
      carousel.addEventListener('mouseenter', () => { hovered = true; clearTimer(); });
      carousel.addEventListener('mouseleave', () => { hovered = false; schedule(); });
      carousel.addEventListener('focusin', () => { focused = true; clearTimer(); });
      carousel.addEventListener('focusout', handleFocusOut);
      carousel.addEventListener('keydown', handleKeyDown);
      carousel.addEventListener('pointerdown', handlePointerDown);
      carousel.addEventListener('pointerup', handlePointerUp);
      carousel.addEventListener('pointercancel', () => { pointerStartX = null; });
      media.addEventListener('change', handlePreferenceChange);
      carousel.__mudjugHeroCleanup = () => {
        clearTimer();
        media.removeEventListener('change', handlePreferenceChange);
      };
      showSlide(activeIndex);
    });
  };

  const destroyHeroCarousels = (scope = document) => {
    const carousels = scope.matches?.('[data-mj-hero-carousel]') ? [scope] : [...scope.querySelectorAll('[data-mj-hero-carousel]')];
    carousels.forEach((carousel) => carousel.__mudjugHeroCleanup?.());
  };

  initializeStyleGuides();
  initializeHeroCarousels();
  document.addEventListener('shopify:section:load', (event) => {
    initializeStyleGuides(event.target);
    initializeHeroCarousels(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => destroyHeroCarousels(event.target));
})();
